import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';

// Event names for Call Transcription tracking
const TRANSCRIPTION_EVENT_NAMES = {
  TAB_OPEN: 'transcription_tab_open',
  TAB_DURATION: 'transcription_tab_duration',
  TAB_ELEMENT_COPY: 'transcription_tab_element_copied',
  NO_TRANSCRIPT_DISPLAYED: 'transcription_tab_no_transcript_displayed',
} as const;

const TRANSCRIPTION_COPY_TYPE = {
  FULL_TRANSCRIPT: 'full_transcript',
  TRANSLATION: 'translation',
  SUMMARY: 'summary',
  KEYWORDS: 'keywords',
} as const;

interface TrackTranscriptionTabOpenParams {
  callState: string;
}

interface TrackTranscriptionTabDurationParams {
  callState: string;
  durationSeconds: number;
}

interface TrackTranscriptionTabCopyParams {
  copyType: (typeof TRANSCRIPTION_COPY_TYPE)[keyof typeof TRANSCRIPTION_COPY_TYPE];
  callState: string;
}

interface TrackNoTranscriptDisplayedParams {
  callState: string;
  hasTokens: boolean;
  callDuration: number;
  errorType: 'no_data' | 'fetch_error' | 'timeout';
  correlationId: string;
}

/**
 * Tracks when the Transcription Tab is opened in the EventPane
 * @param params - The parameters for tracking the transcription tab open
 */
export const trackTranscriptionTabOpen = ({
  callState,
}: TrackTranscriptionTabOpenParams): void => {
  gspx.trackEvent({
    name: TRANSCRIPTION_EVENT_NAMES.TAB_OPEN,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.CALL_TRANSCRIPTION,
    capability: GPX_CAPABILITIES.UNITE_EVENT_PANE,
    route: GPX_ROUTES.EVENT_PANE,
    entities: {
      call_status: callState,
    },
  });
};

/**
 * Tracks the linger time (duration) a user spent on the Transcription Tab
 * @param params - The parameters for tracking the transcription tab linger time
 */
export const trackTranscriptionTabDuration = ({
  callState,
  durationSeconds,
}: TrackTranscriptionTabDurationParams): void => {
  gspx.trackEvent({
    name: TRANSCRIPTION_EVENT_NAMES.TAB_DURATION,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.CALL_TRANSCRIPTION,
    capability: GPX_CAPABILITIES.UNITE_EVENT_PANE,
    route: GPX_ROUTES.EVENT_PANE,
    entities: {
      call_status: callState,
      duration_seconds: durationSeconds,
    },
  });
};

/**
 * Tracks when an element within the Transcription Tab is copied
 * @param params - The parameters for tracking the transcription tab open
 * @param params.callState - The state of the call
 * @param params.copyType - The type of element copied
 */
export const trackTranscriptionTabCopy = ({
  callState,
  copyType,
}: TrackTranscriptionTabCopyParams): void => {
  gspx.trackEvent({
    name: TRANSCRIPTION_EVENT_NAMES.TAB_ELEMENT_COPY,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.CALL_TRANSCRIPTION,
    capability: GPX_CAPABILITIES.UNITE_EVENT_PANE,
    route: GPX_ROUTES.EVENT_PANE,
    entities: {
      element_type: copyType,
      call_status: callState,
    },
  });
};

/**
 * Tracks when the Transcription Tab is opened but no transcript is displayed
 * @param params - The parameters for tracking the missing transcript
 * @param params.callState - The state of the call (active/ended)
 * @param params.hasTokens - Whether any transcript tokens exist
 * @param params.callDuration - Time since call started in seconds
 * @param params.errorType - Type of error encountered
 * @param params.correlationId - Unique ID to correlate with Sentry events
 */
export const trackNoTranscriptDisplayed = ({
  callState,
  hasTokens,
  callDuration,
  errorType,
  correlationId,
}: TrackNoTranscriptDisplayedParams): void => {
  gspx.trackEvent({
    name: TRANSCRIPTION_EVENT_NAMES.NO_TRANSCRIPT_DISPLAYED,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.CALL_TRANSCRIPTION,
    capability: GPX_CAPABILITIES.UNITE_EVENT_PANE,
    route: GPX_ROUTES.EVENT_PANE,
    entities: {
      correlation_id: correlationId,
    },
    additionalKeys: {
      call_status: callState,
      has_tokens: hasTokens,
      call_duration_seconds: callDuration,
      error_type: errorType,
    },
  });
};
